import React from 'react'
import { MDBBtn, MDBCheckbox } from 'mdb-react-ui-kit'

const REMINDER_KINDS = [
  { value: 'day_of', label: 'Day of event' },
  { value: 'offset_days', label: 'N days before event' },
  { value: 'monday_before', label: 'Most recent Monday before event' },
]

const defaultReminder = () => ({
  kind: 'offset_days',
  days: 1,
  send_hour: 8,
  send_minute: 0,
})

const normalizeReminder = r => ({
  kind: r.kind || 'offset_days',
  days: r.days != null ? parseInt(r.days) || 0 : 1,
  send_hour: r.send_hour != null ? parseInt(r.send_hour) || 0 : 8,
  send_minute: r.send_minute != null ? parseInt(r.send_minute) || 0 : 0,
})

export const emptySchedule = () => ({ enabled: false, reminders: [] })

export const parseScheduleString = raw => {
  if (!raw) return emptySchedule()
  if (typeof raw === 'object') {
    return {
      enabled: !!raw.enabled,
      reminders: Array.isArray(raw.reminders) ? raw.reminders.map(normalizeReminder) : [],
    }
  }
  try {
    const parsed = JSON.parse(raw)
    return {
      enabled: !!(parsed && parsed.enabled),
      reminders: Array.isArray(parsed && parsed.reminders) ? parsed.reminders.map(normalizeReminder) : [],
    }
  } catch (_) {
    return emptySchedule()
  }
}

export const serializeSchedule = schedule => {
  if (!schedule || !schedule.enabled || !schedule.reminders || !schedule.reminders.length) {
    return null
  }
  return JSON.stringify({
    enabled: true,
    reminders: schedule.reminders.map(normalizeReminder),
  })
}

const formatTimeValue = (h, m) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`

const NotificationScheduleEditor = ({ value, onChange, calendarTimezone, templateEditUrl, templateSlug }) => {
  const schedule = value || emptySchedule()

  const update = next => onChange && onChange(next)

  const setEnabled = enabled => update({ ...schedule, enabled })

  const updateReminder = (index, patch) => {
    const reminders = schedule.reminders.map((r, i) => (i === index ? { ...r, ...patch } : r))
    update({ ...schedule, reminders })
  }

  const addReminder = () =>
    update({
      ...schedule,
      enabled: true,
      reminders: [...schedule.reminders, defaultReminder()],
    })

  const removeReminder = index =>
    update({
      ...schedule,
      reminders: schedule.reminders.filter((_, i) => i !== index),
    })

  return (
    <div className='notification-schedule-editor'>
      <div className='form-row'>
        <div className='form-group col-md-12'>
          <h4 className='mb-1'>Email Reminders</h4>
          <p className='text-muted mb-2' style={{ fontSize: 13 }}>
            Send automated reminder emails to users in this calendar's audience (active users matching the linked
            usertype <strong>and</strong> division). Reminder times are in the calendar's timezone
            {calendarTimezone ? ` (${calendarTimezone})` : ''}. The email body comes from the template{' '}
            {templateEditUrl ? (
              <a href={templateEditUrl} target='_blank' rel='noopener noreferrer'>
                in Admin Actions &gt; Emails
              </a>
            ) : (
              'in Admin Actions > Emails'
            )}
            ; it is auto-created the first time you save an enabled schedule.
            {templateSlug && (
              <>
                <br />
                <span style={{ fontSize: 12 }}>
                  Template slug: <code>{templateSlug}</code>
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      <div className='form-row'>
        <div className='form-group col-md-12'>
          <MDBCheckbox
            label='Enable reminder emails for this calendar'
            id='notification-schedule-enabled'
            checked={!!schedule.enabled}
            onChange={e => setEnabled(e.target.checked)}
          />
        </div>
      </div>

      {schedule.enabled && (
        <>
          <table className='table table-sm'>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>When</th>
                <th style={{ width: '15%' }}>Days</th>
                <th style={{ width: '20%' }}>Send time</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schedule.reminders.length === 0 && (
                <tr>
                  <td colSpan={4} className='text-muted text-center'>
                    No reminders yet. Click "Add reminder" below.
                  </td>
                </tr>
              )}
              {schedule.reminders.map((r, i) => (
                <tr key={i}>
                  <td>
                    <select
                      className='form-control form-control-sm'
                      value={r.kind}
                      onChange={e => updateReminder(i, { kind: e.target.value })}
                    >
                      {REMINDER_KINDS.map(k => (
                        <option key={k.value} value={k.value}>
                          {k.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {r.kind === 'offset_days' ? (
                      <input
                        type='number'
                        min='0'
                        max='365'
                        className='form-control form-control-sm'
                        value={r.days}
                        onChange={e =>
                          updateReminder(i, {
                            days: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                    ) : (
                      <span className='text-muted'>—</span>
                    )}
                  </td>
                  <td>
                    <input
                      type='time'
                      className='form-control form-control-sm'
                      value={formatTimeValue(r.send_hour, r.send_minute)}
                      onChange={e => {
                        const [h, m] = e.target.value.split(':')
                        updateReminder(i, {
                          send_hour: parseInt(h) || 0,
                          send_minute: parseInt(m) || 0,
                        })
                      }}
                    />
                  </td>
                  <td>
                    <MDBBtn color='danger' size='sm' type='button' onClick={() => removeReminder(i)}>
                      Remove
                    </MDBBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <MDBBtn color='primary' size='sm' type='button' onClick={addReminder}>
            + Add reminder
          </MDBBtn>
        </>
      )}
    </div>
  )
}

export default NotificationScheduleEditor
